import { Task } from '../../types/task';
import { TASK_TEMPLATES_DATA, createTranslatedTasks } from '../../utils/taskTemplates.utils';

export type WeddingType = 'traditional' | 'intimate' | 'destination' | 'eco';

interface TaskTemplate extends Omit<Task, 'id' | 'weddingId' | 'createdAt' | 'updatedAt'> {
  monthsBeforeWedding: number;
  category: TaskCategory;
}

export type TaskCategory = 
  | 'venue'
  | 'attire'
  | 'ceremony'
  | 'reception'
  | 'vendors'
  | 'guests'
  | 'beauty'
  | 'honeymoon'
  | 'legal'
  | 'other';

const traditionalWeddingTasks: TaskTemplate[] = [
  // 12 mois avant
  {
    title: 'Choisir le lieu de réception',
    description: 'Rechercher et visiter différents lieux pour la cérémonie et la réception',
    status: 'todo',
    priority: 'high',
    category: 'venue',
    monthsBeforeWedding: 12,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Définir le budget global',
    description: 'Établir un budget détaillé pour chaque poste de dépense',
    status: 'todo',
    priority: 'high',
    category: 'other',
    monthsBeforeWedding: 12,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Choisir la date',
    description: 'Fixer la date du mariage en fonction des disponibilités du lieu et des prestataires principaux',
    status: 'todo',
    priority: 'high',
    category: 'other',
    monthsBeforeWedding: 12,
    assignees: [],
    dueDate: new Date()
  },

  // 9 mois avant
  {
    title: 'Commander les tenues',
    description: 'Robe de mariée, costume du marié et tenues des témoins',
    status: 'todo',
    priority: 'high',
    category: 'attire',
    monthsBeforeWedding: 9,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Choisir les témoins',
    description: 'Demander officiellement aux témoins de participer au mariage',
    status: 'todo',
    priority: 'medium',
    category: 'ceremony',
    monthsBeforeWedding: 9,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Réserver les prestataires principaux',
    description: 'Photographe, traiteur, DJ/orchestre',
    status: 'todo',
    priority: 'high',
    category: 'vendors',
    monthsBeforeWedding: 9,
    assignees: [],
    dueDate: new Date()
  },

  // 6 mois avant
  {
    title: 'Commander les faire-part',
    description: 'Design, impression et commande des faire-part',
    status: 'todo',
    priority: 'medium',
    category: 'guests',
    monthsBeforeWedding: 6,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Planifier le menu',
    description: 'Définir le menu avec le traiteur, prévoir les alternatives (végétarien, allergies)',
    status: 'todo',
    priority: 'medium',
    category: 'reception',
    monthsBeforeWedding: 6,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Réserver les hébergements',
    description: 'Bloquer des chambres d\'hôtel pour les invités',
    status: 'todo',
    priority: 'medium',
    category: 'guests',
    monthsBeforeWedding: 6,
    assignees: [],
    dueDate: new Date()
  },

  // 3 mois avant
  {
    title: 'Envoyer les faire-part',
    description: 'Envoyer les invitations et commencer à gérer les RSVP',
    status: 'todo',
    priority: 'high',
    category: 'guests',
    monthsBeforeWedding: 3,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Organiser EVJF/EVG',
    description: 'Planifier les enterrements de vie de célibataire',
    status: 'todo',
    priority: 'low',
    category: 'other',
    monthsBeforeWedding: 3,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Finaliser la décoration',
    description: 'Commander les fleurs, la décoration de table et autres éléments décoratifs',
    status: 'todo',
    priority: 'medium',
    category: 'reception',
    monthsBeforeWedding: 3,
    assignees: [],
    dueDate: new Date()
  }
];

const intimateWeddingTasks: TaskTemplate[] = [
  // Tâches spécifiques pour un mariage intime
  {
    title: 'Établir la liste restreinte des invités',
    description: 'Sélectionner les invités les plus proches (famille et amis intimes)',
    status: 'todo',
    priority: 'high',
    category: 'guests',
    monthsBeforeWedding: 6,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Rechercher un lieu intimiste',
    description: 'Trouver un lieu adapté à un petit groupe (maison d\'hôtes, petit domaine)',
    status: 'todo',
    priority: 'high',
    category: 'venue',
    monthsBeforeWedding: 6,
    assignees: [],
    dueDate: new Date()
  },
  // ... autres tâches spécifiques au mariage intime
];

const destinationWeddingTasks: TaskTemplate[] = [
  // Tâches spécifiques pour un mariage destination
  {
    title: 'Choisir la destination',
    description: 'Rechercher et sélectionner le pays/la région du mariage',
    status: 'todo',
    priority: 'high',
    category: 'venue',
    monthsBeforeWedding: 12,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Vérifier les conditions légales',
    description: 'Se renseigner sur les conditions de mariage dans le pays choisi',
    status: 'todo',
    priority: 'high',
    category: 'legal',
    monthsBeforeWedding: 12,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Organiser les voyages',
    description: 'Planifier les transports et hébergements pour les invités',
    status: 'todo',
    priority: 'high',
    category: 'guests',
    monthsBeforeWedding: 9,
    assignees: [],
    dueDate: new Date()
  }
  // ... autres tâches spécifiques au destination wedding
];

const ecoWeddingTasks: TaskTemplate[] = [
  // Tâches spécifiques pour un mariage éco-responsable
  {
    title: 'Rechercher des prestataires éco-responsables',
    description: 'Traiteur bio, décorations naturelles, etc.',
    status: 'todo',
    priority: 'high',
    category: 'vendors',
    monthsBeforeWedding: 9,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Planifier les déplacements durables',
    description: 'Organiser du covoiturage, navettes électriques',
    status: 'todo',
    priority: 'medium',
    category: 'guests',
    monthsBeforeWedding: 6,
    assignees: [],
    dueDate: new Date()
  },
  {
    title: 'Choisir des faire-part écologiques',
    description: 'Papier recyclé, faire-part électroniques',
    status: 'todo',
    priority: 'medium',
    category: 'guests',
    monthsBeforeWedding: 6,
    assignees: [],
    dueDate: new Date()
  }
  // ... autres tâches spécifiques au mariage éco-responsable
];

export const taskTemplates = {
  traditional: traditionalWeddingTasks,
  intimate: intimateWeddingTasks,
  destination: destinationWeddingTasks,
  eco: ecoWeddingTasks
};

export const taskTemplateService = {
  /**
   * Génère une liste de tâches pour un mariage en fonction de sa date et de son type
   * @param weddingDate Date du mariage
   * @param weddingType Type de mariage
   * @returns Liste des tâches avec dates calculées
   */
  generateTasks(weddingDate: Date, weddingType: WeddingType): Omit<Task, 'id'>[] {
    const templates = taskTemplates[weddingType];
    const now = new Date();

    return templates.map(template => {
      const dueDate = new Date(weddingDate);
      dueDate.setMonth(dueDate.getMonth() - template.monthsBeforeWedding);

      return {
        title: template.title,
        description: template.description || '',
        status: dueDate < now ? 'todo' : template.status,
        priority: template.priority,
        category: template.category,
        dueDate,
        assignees: template.assignees,
        createdAt: now,
        updatedAt: now
      };
    });
  },

  /**
   * Génère une liste de tâches traduites pour un mariage
   * @param weddingDate Date du mariage
   * @param weddingType Type de mariage
   * @param language Langue pour les traductions
   * @returns Liste des tâches traduites avec dates calculées
   */
  generateTranslatedTasks(weddingDate: Date, weddingType: WeddingType, language: string): Omit<Task, 'id'>[] {
    // Pour l'instant, on utilise les mêmes templates pour tous les types de mariage
    // On pourrait filtrer par type plus tard si nécessaire
    return createTranslatedTasks(TASK_TEMPLATES_DATA, language, weddingDate, '');
  },

  /**
   * Obtient la liste des tâches pour un type de mariage spécifique
   * @param weddingType Type de mariage
   * @returns Liste des templates de tâches
   */
  getTemplatesByType(weddingType: WeddingType): TaskTemplate[] {
    return taskTemplates[weddingType];
  }
}; 